import { useContext } from 'react';
import { useFragment, graphql, useRelayEnvironment } from 'react-relay';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';

import { FormattedMessage } from 'dibs-react-intl';
import { Button } from 'dibs-elements/exports/Button';
import { trackEvent } from 'dibs-tracking';
import { SharedItemTile } from '../../shared/SharedItemTile/SharedItemTile';
import { FavoritesProvider } from '../../components/global/FavoriteIcons/FavoritesProvider';
import { QuickViewProvider } from '../../global/QuickViewProvider/QuickViewProvider';
import { ServerVarsContext } from '../../global/ServerVarsContext/ServerVarsContext';
import { SbRespPlaceholderTile } from '../SbRespSearchResultContainer/SbRespPlaceholderTile';
import { useShowItemLocationBadge } from '../../shared/hooks/useShowItemLocationBadge';

import {
    VERTICAL_OFFSET_SB_RESP_IMG,
    VERTICAL_OFFSET_MOBILE_IMG,
} from '../../constants/imageConstants';
import { locations } from '../../utils/tracking/shared/favoritesTracking';

import styles from './SbSharedCreatorsResultContainer.scss';

const ITEMS_PER_ROW = 4;
const LAZY_LOAD_ITEM_TILE_INDEX = ITEMS_PER_ROW * 3;

function SbSharedCreatorsResultContainerComponent({
    viewer: viewerRef,
    items: itemsRef,
    itemSearch: itemSearchRef,
    relayVariables,
    refetchInFlight,
    vertical,
    link,
}) {
    const viewer = useFragment(
        graphql`
            fragment SbSharedCreatorsResultContainer_viewer on Viewer {
                ...SharedItemTile_viewer
            }
        `,
        viewerRef
    );

    const itemSearch = useFragment(
        graphql`
            fragment SbSharedCreatorsResultContainer_itemSearch on ItemSearchQueryConnection {
                ...SharedItemTile_itemSearch
                meta {
                    header
                }
            }
        `,
        itemSearchRef
    );

    const items = useFragment(
        graphql`
            fragment SbSharedCreatorsResultContainer_items on ItemSearchResult
            @relay(plural: true) {
                item {
                    serviceId
                    ...SharedItemTile_item
                        @arguments(
                            isTrade: $isTrade
                            userCountryCode: $userCountryCode
                            priceBookName: $priceBookName
                            showSeller: $showSeller
                        )
                }
            }
        `,
        itemsRef
    );
    const { isMobile } = useContext(ServerVarsContext);
    const showItemLocationBadge = useShowItemLocationBadge();

    return (
        <>
            <div className={styles.container}>
                <FavoritesProvider
                    disable={!relayVariables.isClient}
                    environment={useRelayEnvironment()}
                    itemIds={items.map(({ item }) => item?.serviceId)}
                    location={locations.SEARCH_BROWSE}
                    userId={relayVariables.userId}
                    fetchFolder={!isMobile}
                >
                    {({ props: favorites }) => (
                        <QuickViewProvider>
                            {({ quickView }) =>
                                (items || []).map(({ item }, index) => {
                                    if (refetchInFlight && !isMobile) {
                                        return <SbRespPlaceholderTile key={item.serviceId} />;
                                    }
                                    return (
                                        <SharedItemTile
                                            showCreatorName
                                            showSellerName={false}
                                            showItemLocationBadge={showItemLocationBadge}
                                            itemSearch={itemSearch}
                                            isMobile={isMobile}
                                            key={item.serviceId}
                                            item={item}
                                            user={null}
                                            favorites={favorites}
                                            index={index}
                                            quickView={quickView}
                                            lazyLoadStartIndex={
                                                isMobile ? undefined : LAZY_LOAD_ITEM_TILE_INDEX
                                            }
                                            isClient={relayVariables.isClient}
                                            imageLoadVerticalOffset={
                                                isMobile
                                                    ? VERTICAL_OFFSET_MOBILE_IMG
                                                    : VERTICAL_OFFSET_SB_RESP_IMG
                                            }
                                            customClassName={styles.tileWidth}
                                            viewer={viewer}
                                        />
                                    );
                                })
                            }
                        </QuickViewProvider>
                    )}
                </FavoritesProvider>
            </div>
            <div className={styles.shopNowButton}>
                <div className={styles.shopNowDescription}>
                    <FormattedMessage
                        id="sb.SbSharedCreatorsResultContainer.shopNowDescription"
                        defaultMessage="Browse all {vertical} from {creator}"
                        values={{ vertical, creator: itemSearch?.meta?.header }}
                    />
                </div>
                <Button
                    type="secondary"
                    size="medium"
                    href={link}
                    dataTn="shop-now-button"
                    className={styles.shopNowButton}
                    fullWidth={isMobile ? true : false}
                    onClick={() =>
                        trackEvent({
                            category: 'promo interaction',
                            action: 'creator shop now click',
                            label: 'none',
                        })
                    }
                >
                    <FormattedMessage
                        id="sb.SbSharedCreatorsResultContainer.shopNowCTA"
                        defaultMessage="Shop Now"
                    />
                </Button>
            </div>
        </>
    );
}

SbSharedCreatorsResultContainerComponent.propTypes = {
    viewer: PropTypes.object.isRequired,
    vertical: PropTypes.object.isRequired,
    link: PropTypes.string.isRequired,
    itemSearch: PropTypes.object.isRequired,
    items: PropTypes.array,

    relayVariables: PropTypes.object.isRequired,
    refetchInFlight: PropTypes.bool,
};

const mapStateToProps = state => ({
    relayVariables: state?.relayVariables?.variables,
    refetchInFlight: state?.filters?.refetchInFlight,
});

export const SbSharedCreatorsResultContainer = connect(mapStateToProps)(
    SbSharedCreatorsResultContainerComponent
);
